import { useEffect, useCallback, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Tooltip from '@mui/material/Tooltip';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";

import { StoppageDto } from "../../DTOs/Stoppage/StoppageDto";
import { detectionSourceLabel, isAutoDetected } from "../../DTOs/Common/DetectionSource";
import { VerificationIndicator } from "../../components/VerificationIndicator/VerificationIndicator";
import { DataTable, TableRow } from "../../components/DataTable/DataTable";
import TablePage from "../../components/Layout/TablePage";
import { DataTableFilteredRequest, useDataTable } from "../../components/DataTable/TableHook";
import { useAutoPageSize } from "../../components/Hooks/useAutoPageSize";
import { setCurrentView } from "../../state/reducers/viewReducer";
import { RootState } from "../../state/store";
import { stoppagesAjax } from "../../ajax/StoppagesAjax";
import { stoppagesMutations, wasQueued } from "../../offline/stoppagesMutations";
import { cachedPlantFor, patchCachedOperationAvailability } from "../../offline/patchCachedStoppages";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { getContrastTextColor } from "../../utilities/helpers/getContrastTextColor";
import TimeCounter from "../../components/TimeCounter/TimeCounter";
import { DateSelectCrumb } from "../../components/Breadcrumbs/DateSelectCrumb";
import { ShiftSelectCrumb } from "../../components/Breadcrumbs/Shift/ShiftSelectCrumb";
import { ButtonCrumb } from "../../components/Breadcrumbs/ButtonCrumb";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { setSelectedDate, setSelectedShift } from "../../state/reducers/shiftReducer";
import useShiftTimes from "../../components/Hooks/useShiftTimes";
import { LocationDTO } from "../../DTOs/Production/LocationDTO";
import { referencesAjax } from "../../ajax/ReferencesAjax";
import { useOptions } from "../../components/Hooks/useOptions";
import { useSignalRContext } from "../../components/Contexts/SignalRContext";
import StoppageStatusCell from "./TableCellsComponents/StoppageStatusCell";
import UploadStoppageImageButton from "./TableCellsComponents/UploadStoppageImageButton";
import ViewStoppageImagesButton from "./TableCellsComponents/ViewStoppageImagesButton";
import BreakdownCodeCell from "./TableCellsComponents/BreakdownCodeCell";
import StoppageLocationCell from "./TableCellsComponents/StoppageLocationCell";
import RepairsFaultsCell from "./TableCellsComponents/RepairsFaultsCell";
import WorkOrderCell from "./TableCellsComponents/WorkOrderCell";
import WhoWorkedOnItCell from "./TableCellsComponents/WhoWorkedOnItCell";
import StoppagesTableHeader from "./StoppagesTableHeader";
import { DepartmentDTO } from "../../DTOs/DepartmentDTO";
import { Option } from "../../DTOs/Common/OptionResponse";
import { filterCachedRows, matchesText } from "../../offline/filterCachedRows";
import { isOnlineNow } from "../../offline/connectivity";
import { Cancelled } from "../../types/ErrorTypes";

const Stoppages: React.FC = () => {
  const isShiftLocked = useSelector((state: RootState) => state.shift.isLocked);
  const currentView = useSelector((state: RootState) => state.view.currentView);
  const dispatch = useDispatch();
  const { connection } = useSignalRContext();
  const { enqueueSnackbar } = useSnackbar();
  const { shiftStartTime, shiftEndTime, selectedDate, selectedShift } = useShiftTimes();
  const [selectedStatus, setSelectedStatus] = useState<string>("Down & Up");
  const [selectedJobType, setSelectedJobType] = useState<{ value: number; label: string } | null>(null);
  const [machineFilter, setMachineFilter] = useState<string>("");
  const [selectedDepartment, setSelectedDepartment] = useState<DepartmentDTO | null>(null);
  const [unverifiedOnly, setUnverifiedOnly] = useState(false);

  const {
    options: destinationOptions,
    isLoading: isDestinationOptionsLoading
  } = useOptions<LocationDTO, LocationDTO>(
    ['references', 'locations'],
    () => referencesAjax.getLocations({}),
    x => x,
  )

  const { options: jobTypes, isLoading: isJobTypesLoading } = useOptions<Option<number>, { value: number; label: string }>(
    ['references', 'jobTypes'],
    () => referencesAjax.getJobTypes(),
    o => ({ value: o.key, label: o.label }),
  );

  const {
    options: departmentOptions,
    isLoading: isDepartmentOptionsLoading
  } = useOptions<DepartmentDTO, DepartmentDTO>(
    ['references', 'departments'],
    () => referencesAjax.getDepartments({ pageNumber: 1, pageSize: 100 }),
    x => x,
  )

  useEffect(() => {
    const newView = "Stoppages";

    if (currentView !== newView) {
      dispatch(setCurrentView(newView));
    }
  }, [currentView, dispatch]);

  const handleWhoWorkedOnItChange = useCallback(async (stoppage: StoppageDto) => {
    if (!stoppage.stoppageId) {
      enqueueSnackbar('Invalid stoppage selected, please try again', { variant: 'error', autoHideDuration: 3000 });
      return;
    }

    try {
      const updatedStoppage = await stoppagesMutations.updateStoppage(new StoppageDto({
        stoppageId: stoppage.stoppageId,
        stoppageTypeId: stoppage.stoppageTypeId,
        stoppageSubTypeId: stoppage.stoppageSubTypeId,
        plantId: stoppage.plantId,
        locationId: stoppage.locationId,
        repairsFaults: stoppage.repairsFaults,
        workOrder: stoppage.workOrder,
        startTime: stoppage.startTime,
        endTime: stoppage.endTime,
        incident: stoppage.incident,
        whoWorkedOnIt: stoppage.whoWorkedOnIt,
        bookedUpBy: stoppage.bookedUpBy,
        bookedUpDateTime: stoppage.bookedUpDateTime,
        rowVersion: stoppage.rowVersion
      }))

      if (!ErrorResponse.isErrorResponse(updatedStoppage)) {
        enqueueSnackbar('Stoppage updated successfully', { variant: 'success', autoHideDuration: 3000 });
      } else {
        enqueueSnackbar('Stoppage update failed', { variant: 'error', autoHideDuration: 3000 });
      }
    } catch (e) {
      enqueueSnackbar('Stoppage update failed', { variant: 'error', autoHideDuration: 3000 });
    }
  }, [enqueueSnackbar]);

  const handleWorkOrderChange = useCallback(async (stoppage: StoppageDto) => {
    if (!stoppage.stoppageId) {
      enqueueSnackbar('Invalid stoppage selected, please try again', { variant: 'error', autoHideDuration: 3000 });
      return;
    }

    try {
      const updatedStoppage = await stoppagesMutations.updateStoppage(new StoppageDto({
        stoppageId: stoppage.stoppageId,
        stoppageTypeId: stoppage.stoppageTypeId,
        stoppageSubTypeId: stoppage.stoppageSubTypeId,
        plantId: stoppage.plantId,
        locationId: stoppage.locationId,
        repairsFaults: stoppage.repairsFaults,
        workOrder: stoppage.workOrder,
        startTime: stoppage.startTime,
        endTime: stoppage.endTime,
        incident: stoppage.incident,
        whoWorkedOnIt: stoppage.whoWorkedOnIt,
        bookedUpBy: stoppage.bookedUpBy,
        bookedUpDateTime: stoppage.bookedUpDateTime,
        rowVersion: stoppage.rowVersion
      }))

      if (!ErrorResponse.isErrorResponse(updatedStoppage)) {
        enqueueSnackbar('Stoppage updated successfully', { variant: 'success', autoHideDuration: 3000 });
      } else {
        enqueueSnackbar('Stoppage update failed', { variant: 'error', autoHideDuration: 3000 });
      }
    } catch (e) {
      enqueueSnackbar('Stoppage update failed', { variant: 'error', autoHideDuration: 3000 });
    }
  }, [enqueueSnackbar]);

  const handleLocationChange = useCallback(async (stoppage: StoppageDto, locationId: number) => {
    if (!stoppage.stoppageId) {
      enqueueSnackbar('Invalid stoppage selected, please try again', { variant: 'error', autoHideDuration: 3000 });
      return;
    }

    try {
      const updatedStoppage = await stoppagesMutations.updateStoppage(new StoppageDto({
        stoppageId: stoppage.stoppageId,
        stoppageTypeId: stoppage.stoppageTypeId,
        stoppageSubTypeId: stoppage.stoppageSubTypeId,
        plantId: stoppage.plantId,
        locationId: locationId,
        repairsFaults: stoppage.repairsFaults,
        workOrder: stoppage.workOrder,
        startTime: stoppage.startTime,
        endTime: stoppage.endTime,
        incident: stoppage.incident,
        whoWorkedOnIt: stoppage.whoWorkedOnIt,
        bookedUpBy: stoppage.bookedUpBy,
        bookedUpDateTime: stoppage.bookedUpDateTime,
        rowVersion: stoppage.rowVersion
      }))

      if (!updatedStoppage.success || !updatedStoppage.data) {
        enqueueSnackbar('Stoppage update failed', { variant: 'error', autoHideDuration: 3000 });
        return;
      }

      enqueueSnackbar('Stoppage updated successfully', { variant: 'success', autoHideDuration: 3000 });
      updateRowSource((oldRow) => oldRow.stoppageId === stoppage.stoppageId, new StoppageDto({ ...stoppage, locationId }));
    } catch (e) {
      enqueueSnackbar('Stoppage update failed', { variant: 'error', autoHideDuration: 3000 });
    }
  }, [enqueueSnackbar]);

  const handleRepairFaultsChange = useCallback(async (stoppage: StoppageDto) => {
    if (!stoppage.stoppageId) {
      enqueueSnackbar('Invalid stoppage selected, please try again', { variant: 'error', autoHideDuration: 3000 });
      return;
    }

    try {
      const updatedStoppage = await stoppagesMutations.updateStoppage(new StoppageDto({
        stoppageId: stoppage.stoppageId,
        stoppageTypeId: stoppage.stoppageTypeId,
        stoppageSubTypeId: stoppage.stoppageSubTypeId,
        plantId: stoppage.plantId,
        locationId: stoppage.locationId,
        repairsFaults: stoppage.repairsFaults,
        workOrder: stoppage.workOrder,
        startTime: stoppage.startTime,
        endTime: stoppage.endTime,
        incident: stoppage.incident,
        whoWorkedOnIt: stoppage.whoWorkedOnIt,
        bookedUpBy: stoppage.bookedUpBy,
        bookedUpDateTime: stoppage.bookedUpDateTime,
        rowVersion: stoppage.rowVersion
      }))

      if (!ErrorResponse.isErrorResponse(updatedStoppage)) {
        enqueueSnackbar('Stoppage updated successfully', { variant: 'success', autoHideDuration: 3000 });
      } else {
        enqueueSnackbar('Stoppage update failed', { variant: 'error', autoHideDuration: 3000 });
      }
    } catch (e) {
      enqueueSnackbar('Stoppage update failed', { variant: 'error', autoHideDuration: 3000 });
    }
  }, [enqueueSnackbar]);

  const handleStoppageSubTypeChange = useCallback(async (stoppage: StoppageDto, stoppageSubTypeId?: number) => {
    if (!stoppage.stoppageId) {
      enqueueSnackbar('Invalid stoppage selected, please try again', { variant: 'error', autoHideDuration: 3000 });
      return;
    }
    if (!stoppageSubTypeId) {
      enqueueSnackbar('Invalid stoppage subtype selected, please try again', { variant: 'error', autoHideDuration: 3000 });
      return;
    }

    stoppage.stoppageSubTypeId = stoppageSubTypeId;

    try {
      const updatedStoppage = await stoppagesMutations.updateStoppage(new StoppageDto({
        stoppageId: stoppage.stoppageId,
        stoppageTypeId: stoppage.stoppageTypeId,
        stoppageSubTypeId: stoppage.stoppageSubTypeId,
        endTime: stoppage.endTime,
        plantId: stoppage.plantId,
        startTime: stoppage.startTime,
        incident: stoppage.incident,
        locationId: stoppage.locationId,
        repairsFaults: stoppage.repairsFaults,
        workOrder: stoppage.workOrder,
        whoWorkedOnIt: stoppage.whoWorkedOnIt,
        bookedUpBy: stoppage.bookedUpBy,
        bookedUpDateTime: stoppage.bookedUpDateTime,
        rowVersion: stoppage.rowVersion
      }));
      enqueueSnackbar('Stoppage sub type updated successfully', { variant: 'success', autoHideDuration: 3000 });

      if (updatedStoppage.data)
        updateRowSource(oldStoppage => oldStoppage.stoppageId === stoppage.stoppageId, new StoppageDto({ ...stoppage }));
    } catch (error) {
      enqueueSnackbar('Error updating stoppage sub type, please try again', { variant: 'error', autoHideDuration: 3000 });
    }
  }, [enqueueSnackbar]);

  const setAsAvailable = useCallback(async (stoppage: StoppageDto) => {
    let isValid = true;

    if (!stoppage.stoppageSubTypeId) {
      enqueueSnackbar('Invalid stoppage selected, please try again', { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }
    if (!isValid)
      return false;

    const response = await stoppagesMutations.updateStoppage(stoppage);
    if (!response.success && !response.data) {
      response.errors?.forEach((error, index) => {
        enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 })
      })

      return false;
    }

    enqueueSnackbar('Status updated', { variant: 'success', autoHideDuration: 3000 })
    updateRowSource(oldStoppage => oldStoppage.stoppageId === stoppage.stoppageId, response.data!);

    if (wasQueued(response) && stoppage.plantId) {
      patchCachedOperationAvailability(stoppage.plantId, selectedDate.toISODate()!, selectedShift, stoppage.stoppageId);
    }

    return true;
  }, [enqueueSnackbar, selectedDate, selectedShift]);

  const handleBookedUpByChange = useCallback(async (stoppage: StoppageDto) => {
    if (!stoppage.stoppageId) {
      enqueueSnackbar('Invalid stoppage selected, please try again', { variant: 'error', autoHideDuration: 3000 });
      return;
    }
    try {
      const updatedStoppage = await stoppagesMutations.updateStoppage(new StoppageDto({
        stoppageId: stoppage.stoppageId,
        stoppageTypeId: stoppage.stoppageTypeId,
        stoppageSubTypeId: stoppage.stoppageSubTypeId,
        plantId: stoppage.plantId,
        locationId: stoppage.locationId,
        repairsFaults: stoppage.repairsFaults,
        workOrder: stoppage.workOrder,
        startTime: stoppage.startTime,
        endTime: stoppage.endTime,
        incident: stoppage.incident,
        whoWorkedOnIt: stoppage.whoWorkedOnIt,
        bookedUpBy: stoppage.bookedUpBy,
        bookedUpDateTime: stoppage.bookedUpDateTime,
        rowVersion: stoppage.rowVersion
      }))

      if (!ErrorResponse.isErrorResponse(updatedStoppage)) {
        enqueueSnackbar('Stoppage updated successfully', { variant: 'success', autoHideDuration: 3000 });
      } else {
        enqueueSnackbar('Stoppage update failed', { variant: 'error', autoHideDuration: 3000 });
      }
    } catch (e) {
      enqueueSnackbar('Stoppage update failed', { variant: 'error', autoHideDuration: 3000 });
    }
  }, [enqueueSnackbar]);

  const handleStoppageImageStateUpdated = useCallback((updated: StoppageDto) => {
    if (!updated.stoppageId) return;
    updateRowSource(old => old.stoppageId === updated.stoppageId, updated);
  }, []);

  const getRows = useCallback(async (request: DataTableFilteredRequest) => {
    const filters = {
      status: selectedStatus === "Down & Up" ? "" : selectedStatus,
      jobType: selectedJobType?.value,
      machineName: machineFilter,
      departmentId: selectedDepartment?.id,
      unverifiedOnly
    };

    const fromCache = () => {
      return filterCachedRows<StoppageDto>(
        ['dataTable', 'stoppages', 'list', selectedDate.toISODate(), selectedShift,
          'Down & Up', null, '', null, false, 'all', false, null],
        // Note: the only status choices are "Down & Up" (no filter) and "Down", meaning still open.
        row => (filters.status !== 'Down' || row.endTime == null)
          && matchesText((row.plant ?? cachedPlantFor(row.plantId))?.plantRef, filters.machineName)
          && (!filters.departmentId || row.plant?.department?.id === filters.departmentId)
          && (!filters.unverifiedOnly || row.isVerified === false),
        // Note: the maintenance endpoint orders by start time, newest first (StoppagesRepository).
        {
          field: request.sortField,
          direction: request.sortDirection,
          accessors: {
            Machine: row => (row.plant ?? cachedPlantFor(row.plantId))?.plantRef,
            'Date In': row => row.startTime,
            'Time In': row => row.startTime,
            'Time Out': row => row.endTime,
          },
          fallback: row => row.startTime,
          fallbackDirection: 'desc',
        },
      );
    };

    if (!isOnlineNow()) return fromCache();

    try {
      const result = await stoppagesAjax.getStoppagesMaintenance({
        ...request,
        shiftDate: selectedDate.toISODate(),
        dayOrNight: selectedShift[0],
        ...filters
      } as DataTableFilteredRequest);
      return ErrorResponse.isErrorResponse(result) ? fromCache() : result;
    } catch (error) {
      if (error === Cancelled) throw error;
      return fromCache();
    }
  }, [selectedStatus, selectedJobType?.value, machineFilter, selectedDepartment?.id, unverifiedOnly, selectedDate, selectedShift]);

  const translateRows = useCallback((stoppage: StoppageDto): TableRow => {
    const plant = stoppage.plant?.department
      ? stoppage.plant
      : cachedPlantFor(stoppage.plantId) ?? stoppage.plant;
    return {
      id: stoppage.stoppageId || 0,
      rowContainerTailwindClasses: 'animate-none', // Note: entry animation disabled for ExpendableTextAreas to work properly
      values: [
        {
          name: 'Status',
          type: 'custom',
          value: stoppage.endTime ? "UP" : "DOWN",
          tailwindClasses: `flex items-center justify-center w-full basis-[4%]`,
          render: (tableValue) => {
            return (
              <Tooltip
                arrow
                placement="top"
                title={tableValue.value === "UP" ? "Available" : stoppage.stoppageType?.stoppageType ?? ''}
                className="w-full"
              >
                <span
                  style={{
                    backgroundColor: tableValue.value === "UP" ? "#44cc44" : stoppage.stoppageType?.colourHex
                  }}
                  className={`px-2 py-4 text-white font-bold h-full inline-flex w-full items-center justify-center`}>
                  {tableValue.value}
                </span>
              </Tooltip>
            );
          },
        },
        {
          name: "Set Avail'", type: 'custom', value: 'custom', tailwindClasses: 'w-full basis-[3%]',
          render: () => !stoppage.endTime && <StoppageStatusCell
            stoppage={stoppage}
            machineName={plant?.plantRef ?? ''}
            status={stoppage.stoppageSubType?.stoppageSubType ?? ''}
            setAsAvailable={setAsAvailable}
          />
        },
        {
          name: 'Breakdown Code',
          editable: false,
          type: 'custom',
          value: stoppage.stoppageSubType?.stoppageSubType,
          tailwindClasses: 'w-full basis-[9.5%]',
          render: () => {
            return <BreakdownCodeCell
              stoppage={stoppage}
              handleStoppageSubTypeChange={handleStoppageSubTypeChange}
            />
          }
        },
        {
          name: 'Machine',
          editable: false,
          sortable: true,
          type: 'custom',
          value: plant?.plantRef,
          tailwindClasses: 'w-full basis-[5%]',
          render: (tableValue) =>
            <Tooltip
              arrow
              placement="top"
              className="w-full"
              sx={{
                tooltip: {
                  border: '1px solid var(--color-theme-border)',
                  bgcolor: getContrastTextColor(plant?.department?.hexColour ?? '#000000'),
                  boxShadow: 'rgba(0, 0, 0, 0.24) 0px -1px 8px',
                }
              }}
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, -14],
                      },
                    }
                  ],
                },
              }}
              title={
                <>
                  <span style={{ color: getContrastTextColor(plant?.department?.hexColour ?? '#000000') === '#000000' ? '#ffffff' : '#000000' }}>Department: </span>
                  <span
                    className="font-bold"
                    style={{ color: plant?.department?.hexColour }}
                  >{plant?.department?.department}</span>
                </>
              }>
              <span
                className="flex items-center justify-center p-1 h-full inline-flex w-full font-bold rounded shadow border border-gray-400"
                style={{
                  backgroundColor: plant?.department?.hexColour,
                  color: getContrastTextColor(plant?.department?.hexColour ?? '#000000'),
                  borderRadius: 0
                }}
              >
                {tableValue.value}
              </span>
            </Tooltip>
        },
        {
          name: 'Location',
          editable: false,
          type: 'custom',
          value: stoppage,
          tailwindClasses: 'w-full basis-[10%]',
          render: () => <StoppageLocationCell
            stoppage={stoppage}
            locationOptions={destinationOptions}
            isLocationOptionsLoading={isDestinationOptionsLoading}
            handleLocationChange={handleLocationChange}
          />
        },
        {
          name: 'Repairs/Faults',
          editable: false,
          type: 'custom',
          value: stoppage.repairsFaults,
          tailwindClasses: 'w-full basis-[10%]',
          render: () => {
            return <RepairsFaultsCell
              stoppage={stoppage}
              handleRepairFaultsChange={handleRepairFaultsChange}
            />
          }
        },
        {
          name: 'Work Order',
          editable: true,
          type: 'custom',
          value: stoppage.workOrder,
          tailwindClasses: 'w-full basis-[8%]',
          render: () => {
            return <WorkOrderCell
              stoppage={stoppage}
              handleWorkOrderChange={handleWorkOrderChange}
            />
          }
        },
        {
          name: 'Date In',
          type: 'string',
          value: stoppage.startTime.toLocaleString(),
          tailwindClasses: 'w-full basis-[5%]',
          render: (tableValue) => <div className="w-full flex justify-center">{tableValue.value}</div>
        },
        {
          name: 'Time In',
          type: 'string',
          value: stoppage.startTime.toFormat('HH:mm'),
          tailwindClasses: 'w-full basis-[3%]',
          render: (tableValue) => <div className="w-full flex items-center justify-center">{tableValue.value}</div>
        },
        {
          name: 'Time Out',
          type: 'string',
          value: (() => {
            if (!stoppage.endTime || stoppage.endTime.toMillis() > shiftEndTime.toMillis()) return '';
            return stoppage.endTime.toFormat("HH:mm");
          })(),
          tailwindClasses: 'w-full basis-[3%]'
        },
        {
          name: 'Down Hrs',
          editable: false,
          type: 'custom',
          value: `${stoppage.startTime.toFormat("HH:mm")}|${selectedDate}|${selectedShift}`,
          tailwindClasses: 'w-full basis-[4%]',
          render: () => {
            const naturalEnd = stoppage.endTime ?? DateTime.now();
            const startedAt = stoppage.startTime;
            const endedAt = DateTime.min(naturalEnd, shiftEndTime);
            if (endedAt <= startedAt) return <span>0.0h</span>;

            return (
              <div className="flex w-full h-full text-semibold bg-theme-warning justify-center items-center flex-wrap gap-1 font-bold">
                <TimeCounter startedAt={startedAt} endedAt={endedAt} decimals={1} />
              </div>
            );
          }
        },
        {
          name: 'This Shift',
          editable: true,
          type: 'custom',
          value: stoppage.startTime,
          tailwindClasses: 'w-full basis-[4.5%]',
          render: () => {
            const now = DateTime.now();
            const naturalEnd = stoppage.endTime ?? now;

            const clampedStart = DateTime.max(stoppage.startTime, shiftStartTime);
            const clampedEnd = DateTime.min(naturalEnd, shiftEndTime);

            if (clampedEnd <= clampedStart) {
              return <span>0.0</span>;
            }

            return (
              <TimeCounter
                startedAt={clampedStart}
                endedAt={clampedEnd}
                decimals={1}
              />
            );
          }
        },
        {
          name: 'Incident',
          editable: true,
          type: 'custom',
          value: stoppage.incident,
          tailwindClasses: 'w-full basis-[2%]',
          render: () => {
            return (
              stoppage.incident ?
                <FontAwesomeIcon icon={faCircleCheck} className="text-theme-success" size="xl" /> :
                <FontAwesomeIcon icon={faCircleXmark} className="text-theme-danger" size="xl" />
            )
          }
        },
        {
          name: 'Who worked on it',
          editable: true,
          type: 'custom',
          value: stoppage.whoWorkedOnIt,
          tailwindClasses: 'w-full basis-[9%]',
          render: () => {
            return (
              <WhoWorkedOnItCell
                stoppage={stoppage}
                handleWhoWorkedOnItChange={handleWhoWorkedOnItChange}
              />
            )
          }
        },
        {
          name: 'Booked Up by',
          editable: true,
          type: 'string',
          value: stoppage.bookedUpBy,
          tailwindClasses: 'w-full basis-[6%]',
          disabled: isShiftLocked,
          onBlur: async (_, newValue) => {
            if (isShiftLocked) return;
            await handleBookedUpByChange({
              ...stoppage,
              bookedUpBy: newValue
            });
          }
        },
        {
          name: 'Source',
          type: 'custom',
          value: stoppage.isVerified === false ? 'Needs checking' : '',
          tailwindClasses: 'w-full basis-[6%]',
          render: () => (
            <div className="flex items-center justify-center gap-1.5 w-full">
              <div className="text-theme-text-muted text-xs">
                {detectionSourceLabel(stoppage.detectionSource)}
              </div>
              {/* Note: read-only here. There is no per-row accept endpoint on this screen - the trucks
                  table clears a whole hour at once and Stoppages clears by editing - so the mark reports
                  state without pretending to be a control. */}
              {isAutoDetected(stoppage.detectionSource) && (
                <VerificationIndicator isVerified={stoppage.isVerified !== false} subject="stoppage" />
              )}
            </div>
          )
        },
        {
          name: 'Actions',
          type: 'custom',
          value: 'custom',
          tailwindClasses: 'w-full basis-[8%]',
          render: () =>
            <div className="flex h-full w-full justify-center flex-nowrap gap-1 p-1">
              <UploadStoppageImageButton
                stoppage={stoppage}
                onStoppageUpdated={handleStoppageImageStateUpdated}
              />
              <ViewStoppageImagesButton
                stoppage={stoppage}
                onStoppageUpdated={handleStoppageImageStateUpdated}
              />
            </div>
        }
      ]
    };
  }, [
    selectedDate,
    selectedShift,
    isShiftLocked,
    setAsAvailable,
    handleStoppageSubTypeChange,
    destinationOptions,
    isDestinationOptionsLoading,
    handleLocationChange,
    handleRepairFaultsChange,
    handleWorkOrderChange,
    shiftEndTime,
    shiftStartTime,
    handleWhoWorkedOnItChange,
    handleBookedUpByChange,
    handleStoppageImageStateUpdated
  ]);

  const { pageSize: fitPageSize, onAutoPageSize } = useAutoPageSize();

  const {
    displayData,
    isLoading,
    isBackgroundFetching,
    error,
    pageNumber,
    sortField,
    sortDirection,
    setPage,
    pageSize,
    totalCount,
    updateRowSource,
    appendRowSource,
    removeRowSource
  } = useDataTable<StoppageDto>({
    queryKey: ['stoppages', 'list', selectedDate.toISODate(), selectedShift, selectedStatus, selectedJobType?.value ?? null, machineFilter, selectedDepartment?.id ?? null, unverifiedOnly],
    fetchFn: getRows,
    translateFn: translateRows,
    initialPage: 1,
    pageSize: fitPageSize,
    clientPaginate: true,
  });

  const displayDataRef = useRef(displayData);
  const localSelectedShiftStartTime = useRef(shiftStartTime);
  const localSelectedShiftEndTime = useRef(shiftEndTime);

  useEffect(() => {
    displayDataRef.current = displayData;
  }, [displayData]);

  useEffect(() => {
    localSelectedShiftStartTime.current = shiftStartTime;
    localSelectedShiftEndTime.current = shiftEndTime;
  }, [shiftStartTime, shiftEndTime]);

  const handleStoppageUpdated = useCallback(async (updatedRow: StoppageDto) => {
    try {
      const stoppageIdToUpdate = updatedRow.stoppageId;

      if (stoppageIdToUpdate) {
        if (updatedRow.startTime >= localSelectedShiftEndTime.current || (updatedRow.endTime && updatedRow.endTime < localSelectedShiftStartTime.current)) {
          removeRowSource(oldRow => oldRow.stoppageId === stoppageIdToUpdate);
          enqueueSnackbar(`Stoppage information for ${updatedRow.plant?.plantRef} has been updated in the past shift`, { variant: 'info', autoHideDuration: 5000 });
          return;
        }
        updateRowSource(oldRow => oldRow.stoppageId === updatedRow.stoppageId, updatedRow);
        enqueueSnackbar(`${updatedRow.plant?.plantRef} has been updated`, { variant: 'info', autoHideDuration: 5000 });
      }
    } catch (error) {
      if (error !== "CANCELLED")
        enqueueSnackbar("Live updates for this page have been interrupted. Please refresh the page", { variant: 'error', autoHideDuration: 5000 });
    }
  }, [updateRowSource, enqueueSnackbar, removeRowSource]);

  const handleStoppageAdded = useCallback((newStoppage: StoppageDto) => {
    if (!newStoppage || !newStoppage.stoppageId || newStoppage.startTime >= localSelectedShiftEndTime.current || (newStoppage.endTime && newStoppage.endTime < localSelectedShiftStartTime.current)) return;

    appendRowSource(newStoppage);
    enqueueSnackbar(`A new stoppage for ${newStoppage.plant?.plantRef} has been added`, { variant: 'info', autoHideDuration: 5000 });
  }, [appendRowSource, enqueueSnackbar]);

  const handleStoppageDeleted = useCallback((deletedStoppageId: number) => {
    if (!deletedStoppageId || deletedStoppageId <= 0) return;

    const deletedStoppage = displayDataRef.current.find(row => row.id === deletedStoppageId);

    if (!deletedStoppage) return;
    const deletedStoppagePlantName = deletedStoppage?.values.find(v => v.name === 'Machine')?.value;

    removeRowSource(oldRow => oldRow.stoppageId === deletedStoppageId);
    enqueueSnackbar(`A stoppage entry ${deletedStoppagePlantName ? `for ${deletedStoppagePlantName} ` : ''}has been deleted`, { variant: 'info', autoHideDuration: 5000 });
  }, [removeRowSource, enqueueSnackbar]);

  useEffect(() => {
    if (!connection) return;
    connection.on("DataUpdatedAsync", handleStoppageUpdated);
    connection.on("DataInsertedAsync", handleStoppageAdded);
    connection.on("DataDeletedAsync", handleStoppageDeleted);

    return () => {
      connection.off("DataUpdatedAsync", handleStoppageUpdated);
      connection.off("DataInsertedAsync", handleStoppageAdded);
      connection.off("DataDeletedAsync", handleStoppageDeleted);
    };
  }, [connection, handleStoppageAdded, handleStoppageDeleted, handleStoppageUpdated]);

  return (
    <TablePage>
      <Breadcrumbs>
        <DateSelectCrumb
          selectedDate={selectedDate}
          setSelectedDate={date => {
            dispatch(setSelectedDate(date.toISODate()!))
          }}
        />
        <ShiftSelectCrumb
          selectedShift={selectedShift}
          setSelectedShift={shift => dispatch(setSelectedShift(shift))}
        />
        <ButtonCrumb buttonText={"Stoppages List"} />
      </Breadcrumbs>
      <DataTable
        data={displayData}
        isLoading={isLoading}
        isBackgroundFetching={isBackgroundFetching}
        error={error}
        pageNumber={pageNumber}
        setPage={setPage}
        pageSize={pageSize}
        totalCount={totalCount}
        sortField={sortField}
        sortDirection={sortDirection}
        minTableWidthPixels={1920}
        disableContainer={true}
        fillHeight
        autoPageSize
        onAutoPageSize={onAutoPageSize}
        estimatedRowHeightPx={52}
        tableHeaderContent={
          <StoppagesTableHeader
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
            selectedJobType={selectedJobType}
            setSelectedJobType={setSelectedJobType}
            jobTypeOptions={[...jobTypes].sort((a, b) => a.label.localeCompare(b.label))}
            isJobTypeOptionsLoading={isJobTypesLoading}
            machineFilter={machineFilter}
            onMachineFilterChange={setMachineFilter}
            selectedDepartment={selectedDepartment}
            setSelectedDepartment={setSelectedDepartment}
            departmentOptions={departmentOptions}
            isDepartmentOptionsLoading={isDepartmentOptionsLoading}
            unverifiedOnly={unverifiedOnly}
            setUnverifiedOnly={setUnverifiedOnly}
          />
        }
      />
    </TablePage>
  )
};

export default Stoppages;